import { HttpClient } from '@angular/common/http';
import { Injectable, Inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { UserAnjana } from '../interfaces/UserAnjana';
import { ToastService } from './script/toast.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly sessionDurationMs = 24 * 60 * 60 * 1000;
  private isSessionExpirationLogoutInProgress = false;

  // Reactive signals
  private isLoggedSignal = signal<boolean>(this.isAuthenticated());
  private currentUserSignal = signal<Partial<UserAnjana> | null>(this.getCurrentUser());

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  // Validate token and set user
  async validateToken(body: { nif: string; org: string; token: string }): Promise<UserAnjana> {
    const headers = { 'Content-Type': 'application/json' };

    const user = await firstValueFrom(
      this.http.post<UserAnjana>(`${environment.urlGetToken}/api/aurora/tokens_manager`, body, {
        headers,
        withCredentials: true,
      })
    );
    const userData: Partial<UserAnjana> = {
      givenName: user.givenName,
      surname1: user.surname1,
      surname2: user.surname2,
      roleCode: user.roleCode,
      identificationMethod: user.identificationMethod,
      orgCode: body.org,
      orgName: user.organismName,
      stationWork: user.puesto,
      email: user.email,
      department: user.departamento,
      rpt: user.rpt,
      roles: user.roles,
      nif: user.eIdentifier,
      serviceName: user.servicio,
      generalDirectorateCode: user.codigo_direccion_general,
    };

    localStorage.setItem('userData', JSON.stringify(userData));
    localStorage.setItem('loginTimestamp', Date.now().toString());

    // Update signals
    this.currentUserSignal.set(userData);
    this.isLoggedSignal.set(true);

    return user;
  }

  // Get current user (from storage)
  getCurrentUser(): Partial<UserAnjana> | null {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  }

  // Set current user manually
  setUserData(userData: Partial<UserAnjana>) {
    localStorage.setItem('userData', JSON.stringify(userData));
    this.currentUserSignal.set(userData);
    this.isLoggedSignal.set(true);
  }

  // NEW METHOD: reactive setter (used by Navbar or new features)
  setCurrentUser(user: Partial<UserAnjana>) {
    this.setUserData(user);
  }

  // Check authentication
  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    return !!user;
  }

  isLoginTimeExpired(): boolean {
    const loginTimeStr = localStorage.getItem('loginTime');

    if (!loginTimeStr) {
      return false;
    }

    const loginTime = Number(loginTimeStr);

    if (!Number.isFinite(loginTime)) {
      return true;
    }

    return Date.now() - loginTime >= this.sessionDurationMs;
  }

  logoutIfLoginTimeExpired(): boolean {
    if (!this.isLoginTimeExpired()) {
      return false;
    }

    this.logoutExpiredSession();
    return true;
  }

  logoutExpiredSession(): void {
    if (this.isSessionExpirationLogoutInProgress) {
      return;
    }

    this.isSessionExpirationLogoutInProgress = true;
    this.toastService.showError('Sesión expirada', 'Inicia sesión de nuevo para continuar.');

    setTimeout(() => this.logout(), 2000);
  }

  // Logout logic
  logout(): void {
    const currentUser = this.getCurrentUser();
    localStorage.clear();

    // Clear cookies
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      this.document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });

    // Update signals
    this.currentUserSignal.set(null);
    this.isLoggedSignal.set(false);

    if (currentUser) {
      this.document.location.href = `https://${environment.url_serv}/rest/identification/HAL/${btoa(
        environment.home_url
      )}/${currentUser.identificationMethod}/logout`;
    }
  }

  // Public readonly signals
  isLogged = this.isLoggedSignal.asReadonly();
  currentUser = this.currentUserSignal.asReadonly(); // renamed "user" → "currentUser" for clarity

  // reactive getter for convenience
  get currentUserValue(): Partial<UserAnjana> | null {
    return this.currentUserSignal();
  }

  // clear all session data without redirect
  clearSession(): void {
    localStorage.removeItem('userData');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('loginTimestamp');
    this.currentUserSignal.set(null);
    this.isLoggedSignal.set(false);
  }

  // Fetch protected data with credentials
  async getProtectedData<T>(endpoint: string): Promise<T> {
    return firstValueFrom(
      this.http.get<T>(`${environment.urlGetToken}${endpoint}`, {
        withCredentials: true,
      })
    );
  }
}
